import React, { useRef, useState } from "react";
import { Alert, Button, Form, InputGroup } from "react-bootstrap";
import {
  MdClose,
  MdOndemandVideo,
  MdPlayArrow,
  MdVideoLibrary,
} from "react-icons/md";

import UploadProgress from "./UploadProgress";
import uploadVideoToCloudinary, {
  ADVISORY_MAX_DURATION_SEC,
  MAX_PRODUCT_VIDEOS,
  MAX_VIDEO_SIZE_MB,
  buildCloudinaryVideoEntry,
  buildYouTubeVideoEntry,
  warmCloudinaryVideo,
} from "../utilities/videoUpload";

/**
 * ProductVideoSection — the one product-video control, shared by ALL four
 * vertical product forms (brand / restaurant / retailer-wholesaler /
 * healthcare), Create and Edit alike. Renders under the image section.
 *
 * Emits value up exactly like the image controls do: the parent owns the
 * array, this component only calls `onChange(nextVideos)`.
 *
 * Value shape is the FROZEN video contract (mirrored by the server
 * sanitizer in utils/productVideos.js and the mobile clients):
 *
 *   { kind: "CLOUDINARY" | "YOUTUBE",
 *     url, publicId, thumbnailUrl, durationSec }
 *
 * Unlike images (which stage dataURLs and upload at submit), videos upload
 * IMMEDIATELY on pick: a 50 MB dataURL sitting in React state would freeze
 * the form long before submit. So the array in parent state always holds
 * finished, contract-shaped entries and the submit handler sends it as-is.
 */

const ACCEPT = ".mp4,.mov,.webm,video/mp4,video/quicktime,video/webm";

const ProductVideoSection = ({
  label = "Product videos (optional)",
  /** Contract-shaped entries; the parent owns this array. */
  videos,
  /** (nextVideos) => void */
  onChange,
  disabled = false,
}) => {
  const safeVideos = Array.isArray(videos) ? videos : [];
  const inputRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [ytError, setYtError] = useState("");

  const atCap = safeVideos.length >= MAX_PRODUCT_VIDEOS;
  const locked = disabled || uploading;

  const handlePickVideo = async (event) => {
    const file = event.target.files?.[0];
    /* Reset so picking the same file twice still fires onChange. */
    if (event.target) event.target.value = "";
    if (!file) return;
    if (atCap) {
      setError(`Maximum of ${MAX_PRODUCT_VIDEOS} videos per product.`);
      return;
    }

    setError("");
    setWarning("");
    setUploading(true);
    setUploadProgress(0);
    try {
      const result = await uploadVideoToCloudinary(file, {
        onProgress: (percent) => setUploadProgress(percent),
      });
      const entry = buildCloudinaryVideoEntry(result);
      if (!entry) {
        throw new Error("Video upload returned no URL. Please try again.");
      }
      if (result.durationWarning) {
        setWarning(result.durationWarning);
      }
      onChange([...safeVideos, entry]);
      // Kick off the H.264 rendition now (see warmCloudinaryVideo). If
      // Cloudinary says it cannot convert this clip, say so here, while the
      // operator can still swap the file — not on a shopper's screen.
      warmCloudinaryVideo(entry.url).then((problem) => {
        if (problem) setWarning(problem);
      });
    } catch (err) {
      setError(err?.message || "Video upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleAddYouTube = () => {
    if (atCap) {
      setYtError(`Maximum of ${MAX_PRODUCT_VIDEOS} videos per product.`);
      return;
    }
    const entry = buildYouTubeVideoEntry(ytUrl);
    if (!entry) {
      setYtError(
        "That does not look like a YouTube video link. Paste a watch, youtu.be, Shorts, or embed URL."
      );
      return;
    }
    if (safeVideos.some((v) => v?.url === entry.url)) {
      setYtError("That YouTube video is already on this product.");
      return;
    }
    setYtError("");
    setYtUrl("");
    onChange([...safeVideos, entry]);
  };

  const handleRemove = (index) => {
    onChange(safeVideos.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-3">
      <div style={styles.headerRow}>
        <label className="form-label mb-0" style={styles.headerLabel}>
          <MdVideoLibrary size={16} style={{ marginRight: 6, marginTop: -2 }} />
          {label}
        </label>
        <span style={styles.counter}>
          {safeVideos.length} of {MAX_PRODUCT_VIDEOS} videos
        </span>
      </div>

      {safeVideos.length > 0 && (
        <div style={styles.grid}>
          {safeVideos.map((video, i) => (
            <div key={`${video?.url || "video"}-${i}`} style={styles.tile}>
              {video?.thumbnailUrl ? (
                <img
                  src={video.thumbnailUrl}
                  alt={`Product video ${i + 1}`}
                  style={styles.tileImg}
                  draggable={false}
                />
              ) : (
                <div style={styles.tileFallback}>
                  <MdOndemandVideo size={30} color="#94a3b8" />
                </div>
              )}

              <span style={styles.playGlyph} aria-hidden="true">
                <MdPlayArrow size={22} />
              </span>

              <span
                style={{
                  ...styles.kindBadge,
                  background:
                    video?.kind === "YOUTUBE" ? "#b91c1c" : "#0a578b",
                }}
              >
                {video?.kind === "YOUTUBE" ? "YOUTUBE" : "CLOUDINARY"}
              </span>

              {Number(video?.durationSec) > 0 && (
                <span style={styles.durationBadge}>
                  {Math.round(Number(video.durationSec))}s
                </span>
              )}

              <button
                type="button"
                onClick={() => handleRemove(i)}
                disabled={locked}
                style={styles.removeBtn}
                aria-label={`Remove product video ${i + 1}`}
              >
                <MdClose size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={styles.actionsRow}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={handlePickVideo}
          disabled={locked || atCap}
          style={{ display: "none" }}
        />
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={locked || atCap}
          style={styles.uploadBtn}
        >
          <MdOndemandVideo size={16} style={{ marginRight: 6, marginTop: -2 }} />
          {uploading ? "Uploading video..." : "Upload video"}
        </Button>
        <span style={styles.hint}>
          MP4, MOV, or WebM up to {MAX_VIDEO_SIZE_MB} MB. Keep it under{" "}
          {ADVISORY_MAX_DURATION_SEC}s for best results.
        </span>
      </div>

      {uploading && <UploadProgress percent={uploadProgress} />}

      <div style={styles.ytRow}>
        <InputGroup size="sm">
          <Form.Control
            type="url"
            placeholder="Or paste a YouTube link (watch, youtu.be, Shorts, or embed)"
            value={ytUrl}
            onChange={(e) => {
              setYtUrl(e.target.value);
              if (ytError) setYtError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!locked && !atCap && ytUrl.trim()) handleAddYouTube();
              }
            }}
            disabled={locked || atCap}
            isInvalid={Boolean(ytError)}
            aria-label="YouTube video URL"
          />
          <Button
            type="button"
            variant="outline-primary"
            onClick={handleAddYouTube}
            disabled={locked || atCap || !ytUrl.trim()}
          >
            Add
          </Button>
        </InputGroup>
        {ytError && <div style={styles.inlineError}>{ytError}</div>}
      </div>

      {atCap && (
        <div style={styles.capNote}>
          Video limit reached. Remove one to add another.
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mt-2 mb-0" style={styles.alert}>
          {error}
        </Alert>
      )}
      {warning && (
        <Alert variant="warning" className="mt-2 mb-0" style={styles.alert}>
          {warning}
        </Alert>
      )}
    </div>
  );
};

const styles = {
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 8,
  },
  headerLabel: {
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 600,
  },
  counter: {
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    fontVariantNumeric: "tabular-nums",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: 10,
    marginBottom: 10,
    maxWidth: 480,
  },
  tile: {
    position: "relative",
    aspectRatio: "16 / 9",
    borderRadius: 8,
    overflow: "hidden",
    background: "#0f172a",
    border: "1px solid #e2e8f0",
  },
  tileImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  tileFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#1e293b",
  },
  playGlyph: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.6)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  kindBadge: {
    position: "absolute",
    top: 4,
    left: 4,
    padding: "1px 6px",
    borderRadius: 4,
    color: "#fff",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.04em",
  },
  durationBadge: {
    position: "absolute",
    bottom: 4,
    left: 4,
    padding: "1px 5px",
    borderRadius: 4,
    background: "rgba(0,0,0,0.65)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
  },
  removeBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "none",
    background: "rgba(0,0,0,0.65)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
  },
  uploadBtn: {
    display: "inline-flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  hint: {
    fontSize: 11,
    color: "#64748b",
  },
  ytRow: {
    marginTop: 10,
    maxWidth: 480,
  },
  inlineError: {
    marginTop: 4,
    fontSize: 12,
    color: "#dc3545",
  },
  capNote: {
    marginTop: 6,
    fontSize: 11,
    color: "#64748b",
  },
  alert: {
    fontSize: 12,
    padding: "8px 12px",
    maxWidth: 480,
  },
};

export default ProductVideoSection;
